import { pick } from "lodash";
import { ApolloClient } from "@apollo/client";
import { GigActionsQuery } from "gql-gen";
import { createElement } from "react";

import actionsQuery from "./actions.gql";
import { RequestApproval } from "./RequestApproval";
import { ApproveAction } from "./Approve";
import { CancelAction } from "./Cancel";
import { AssignAction } from "./Assign";
import { AuthorizePayment } from "./AuthorizePayment";
import { DeleteAction } from "./Delete";
import { DuplicateAction } from "./Duplicate";
import { UntrashAction } from "./Untrash";
import { UpdateAction } from "./Update";
import { UpdateOwnerAction } from "./UpdateOwner";
import { DownloadReceiptsAction } from "./DownloadReceipts";
import { AvailableAction, IAction } from "modules/Connection/types";
import { UndoReviewAction } from "./UndoReview";
import { OwnerVerify } from "./OwnerVerify";
import { OwnerVerified } from "./OwnerVerified";
import { SelectPartnerAction } from "./SelectPartner";
import { VerifyAction } from "./Verify";
import { AddToInvoice } from "./AddToInvoice";
import { Confirmation } from "./Confirmation";
import { Billing } from "./Billing";
import { ToggleClosed, Close } from "./ToggleClosed";

const allActions = [
  RequestApproval,
  ApproveAction,
  CancelAction,
  DeleteAction,
  AssignAction,
  SelectPartnerAction,
  AuthorizePayment,
  DuplicateAction,
  UntrashAction,
  UpdateAction,
  UpdateOwnerAction,
  DownloadReceiptsAction,
  UndoReviewAction,
  OwnerVerify,
  OwnerVerified,
  VerifyAction,
  AddToInvoice,
  Billing,
  ToggleClosed(Close.Close),
  ToggleClosed(Close.UndoClose),
  Confirmation,
];

const index: { [key: string]: IAction } = {};

for (const action of allActions) {
  index[action.key] = action;
}

function componentReturnsNull(Component: any): boolean {
  try {
    const element = createElement(Component, {} as any);

    if (typeof element.type === 'function') {
      const result = (element.type as Function)(element.props);
      return result === null;
    }

    return false;
  } catch (e) {
    return false;
  }
}

export async function selectGigActions(variables: unknown, client: ApolloClient<unknown>, isBatchAction?: boolean): Promise<AvailableAction[]> {
  const { data } = await client.query<GigActionsQuery>({
    query: actionsQuery,
    variables: pick(variables, ["programIds", "filters"]),
    fetchPolicy: "network-only",
  });

  if (data && data.gigActions) {
    return data.gigActions
      .map(({ actionKey, actionKeyCount, actionPrimary, actionOrder }) => ({
        action: index[actionKey],
        count: actionKeyCount,
        primary: actionPrimary,
        order: actionOrder,
      }))
      .filter(({ action }) => {
        // Hide "update" action in single gig view (only show in batch actions)
        // isBatchAction is undefined for single gig view, true for batch actions
        if (action.key === 'update') {
          return isBatchAction === true;
        }
        return !componentReturnsNull(action.component);
      });
  }
  return [];
}
